<script lang="ts">
  import { clickOutsideAction } from 'svelte-tel-input/utils';
  import { TelInput, isSelected, normalizedCountries } from 'svelte-tel-input';
  import type {
    DetailedValue,
    CountryCode,
    E164Number,
    TelInputOptions,
    Country
  } from 'svelte-tel-input/types';
  import 'svelte-tel-input/styles/flags.css';
  import globeImg from '../../../assets/globe.webp';

  export let clickOutside = true;
  export let closeOnClick = true;
  export let disabled = false;
  export let detailedValue: DetailedValue | null = null;
  export let value: E164Number | null;
  export let searchPlaceholder: string | null = 'Search';
  export let selectedCountry: CountryCode | null;
  export let valid: boolean;
  export let options: TelInputOptions;
  export let onChange: (selectedCountry: CountryCode) => void;
  export let onSame: (option: CountryCode) => void;
  export let label: string;
  export let id: string;
  export let required: boolean;
  export const country: CountryCode | '' = '';
  export let error: string | undefined;

  let searchText = '';
  let isOpen = false;

  $: selectedCountryDialCode =
    normalizedCountries.find((el) => el.iso2 === selectedCountry)?.dialCode || null;

  const toggleDropDown = (e?: Event) => {
    e?.preventDefault();
    if (disabled) return;
    isOpen = !isOpen;
  };

  const closeDropdown = (e?: Event) => {
    if (isOpen) {
      e?.preventDefault();
      isOpen = false;
      searchText = '';
    }
  };

  const selectClick = () => {
    if (closeOnClick) closeDropdown();
  };

  const closeOnClickOutside = () => {
    if (clickOutside) {
      closeDropdown();
    }
  };

  const sortCountries = (countries: Country[], text: string) => {
    const normalizedText = text.trim().toLowerCase();
    if (!normalizedText) {
      return countries.sort((a, b) => a.label.localeCompare(b.label));
    }
    return countries.sort((a, b) => {
      const aNameLower = a.name.toLowerCase();
      const bNameLower = b.name.toLowerCase();
      const aStartsWith = aNameLower.startsWith(normalizedText);
      const bStartsWith = bNameLower.startsWith(normalizedText);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      const aIndex = aNameLower.indexOf(normalizedText);
      const bIndex = bNameLower.indexOf(normalizedText);
      if (aIndex === -1 && bIndex === -1) return a.id.localeCompare(b.id);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      const aWeight = aIndex + (aStartsWith ? 0 : 1);
      const bWeight = bIndex + (bStartsWith ? 0 : 1);
      return aWeight - bWeight;
    });
  };

  const handleSelect = (val: CountryCode, e?: Event) => {
    if (disabled) return;
    e?.preventDefault();
    if (
      selectedCountry === undefined ||
      selectedCountry === null ||
      (typeof selectedCountry === typeof val && selectedCountry !== val)
    ) {
      selectedCountry = val;
      onChange(val);
      selectClick();
    } else {
      onSame(val);
      selectClick();
    }
  };
</script>

<div class="flex flex-col">
  <label for={id} class="mb-2">
    {label}
    {#if !required}<span class="text-gray-500">(Optional)</span>{/if}
  </label>
  <div
    class="flex relative rounded-lg {valid
      ? ``
      : ` ring-pink-500 dark:ring-pink-500 ring-1 focus-within:ring-offset-1 focus-within:ring-offset-pink-500/50 focus-within:ring-1`}"
  >
    <div class="flex" use:clickOutsideAction={closeOnClickOutside}>
      <button
        id="states-button"
        data-dropdown-toggle="dropdown-states"
        class="relative flex-shrink-0 overflow-hidden z-10 whitespace-nowrap inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-white bg-[#121212] border border-gray-500 rounded-l-lg hover:bg-gray-200 focus:outline-none mr-4"
        type="button"
        role="combobox"
        aria-controls="dropdown-countries"
        aria-expanded="false"
        aria-haspopup="false"
        on:click={toggleDropDown}
      >
        {#if selectedCountry && selectedCountry !== null}
          <div class="inline-flex items-center text-left">
            <span class="flag flag-{selectedCountry.toLowerCase()} flex-shrink-0 mr-3"></span>
            {#if options.format === 'national'}
              <span class=" text-white">+{selectedCountryDialCode}</span>
            {/if}
          </div>
        {:else}
          <img src={globeImg} alt="Globe" />
        {/if}
        <svg
          aria-hidden="true"
          class="ml-1 w-4 h-4 {isOpen ? 'rotate-180' : ''}"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 111.414 1.414l-4 4a1 0 01-1.414 0l-4-4a1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
      {#if isOpen}
        <div
          id="dropdown-countries"
          class="absolute z-10 max-w-fit rounded divide-y divide-gray-100 shadow bg-gray-700 overflow-hidden translate-y-11"
          data-popper-reference-hidden=""
          data-popper-escaped=""
          data-popper-placement="bottom"
          aria-orientation="vertical"
          aria-labelledby="country-button"
          tabindex="-1"
        >
          <div
            class="text-sm text-white max-h-48 overflow-y-auto bg-black"
            aria-labelledby="countries-button"
            role="listbox"
          >
            <input
              aria-autocomplete="list"
              type="text"
              class="px-4 py-2 text-gray-900 focus:outline-none w-full sticky top-0"
              bind:value={searchText}
              placeholder={searchPlaceholder}
            />
            {#each sortCountries(normalizedCountries, searchText) as country (country.id)}
              {@const isActive = isSelected(country.iso2, selectedCountry)}
              <div id="country-{country.iso2}" role="option" aria-selected={isActive}>
                <button
                  value={country.iso2}
                  type="button"
                  class="inline-flex py-2 px-4 w-full text-sm hover:bg-gray-200
                               active:bg-gray-80 overflow-hidden
                              {isActive
                    ? 'bg-gray-200 text-white'
                    : 'hover:text-white text-gray-400'}"
                  on:click={(e) => {
                    handleSelect(country.iso2, e);
                  }}
                >
                  <div class="inline-flex items-center text-left">
                    <span class="flag flag-{country.iso2.toLowerCase()} flex-shrink-0 mr-3"></span>
                    <span class="mr-2">{country.name}</span>
                    <span class="text-gray-500">+{country.dialCode}</span>
                  </div>
                </button>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>

    <TelInput
      bind:country={selectedCountry}
      bind:detailedValue
      bind:value
      bind:valid
      {options}
      required
      class="text-sm rounded-r-lg block w-full p-2.5 focus:outline-none border border-gray-300 border-l-gray-700 border-gray-60 bg-gray-700 
      placeholder-gray-400 text-white"
      on:blur={() => {
        if (value === null) {
          value = '';
        }
      }}
    />
  </div>
  {#if error}
    <span class="text-red-500 text-sm mt-1">{error}</span>
  {/if}
</div>
